import React from 'react';

/**
 * AbnormalFlag
 * ------------
 * The at-a-glance "this result is out of range" marker, shown everywhere a lab or
 * referral order is listed so staff never have to open the PDF to learn that a
 * result needs attention.
 *
 * Reads the LIS verdict written by utils/labAbnormal on ingest (blueprint §7):
 *   abnormal_status = 'abnormal'      -> '* ABNORMAL'
 *   abnormal_status = 'indeterminate' -> 'REVIEW' (see the note below)
 *   abnormal_status = 'normal'        -> nothing (an unremarkable result stays quiet)
 *   abnormal_status = NULL            -> nothing (no results in yet, or a legacy
 *                                       order resulted before abnormal detection)
 *
 * NOTE on 'indeterminate': the LIS uses it for a result it could NOT evaluate (no
 * usable flag and an unparseable value/range — hormone panels, unmapped analytes).
 * It is deliberately NOT the same as normal, so it gets its own amber REVIEW marker:
 * rendering nothing made an unevaluated result look like a clean one in the worklist,
 * which is the one reading of it that is actively unsafe.
 *
 * Props:
 *   status     abnormal_status from the order row
 *   abnormal   the 0/1 abnormal column; used as a fallback when status is absent
 *              (rows resulted before abnormal_status was backfilled)
 *   inline     true to render without the leading margin (for use inside a cell
 *              that already has its own spacing)
 */
const baseStyle = (inline) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '3px',
  marginLeft: inline ? 0 : '6px',
  padding: '1px 6px',
  borderRadius: '4px',
  fontSize: '11px',
  fontWeight: 700,
  letterSpacing: '0.03em',
  lineHeight: 1.6,
  whiteSpace: 'nowrap',
});

const AbnormalFlag = ({ status, abnormal, inline = false }) => {
  const isAbnormal = status === 'abnormal' || (status == null && Number(abnormal) === 1);

  if (isAbnormal) {
    return (
      <span
        title="Abnormal result — at least one analyte is outside its reference range"
        style={{
          ...baseStyle(inline),
          background: '#fee2e2',
          color: '#b91c1c',
          border: '1px solid #fca5a5',
        }}
      >
        <span aria-hidden="true">*</span>
        ABNORMAL
      </span>
    );
  }

  if (status === 'indeterminate') {
    return (
      <span
        title="Needs review — at least one analyte could not be evaluated automatically (no usable flag and no parseable reference range). This is not a normal result."
        style={{
          ...baseStyle(inline),
          background: '#fef3c7',
          color: '#92400e',
          border: '1px solid #fcd34d',
        }}
      >
        <span aria-hidden="true">⚑</span>
        REVIEW
      </span>
    );
  }

  return null;
};

export default AbnormalFlag;
